// import { type PropsWithChildren } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { isAfter } from 'date-fns';
import { useAuthorization, userRoleSelector } from '@/hooks/useAuthorization.hook';
import { type CallbackFunc } from '@/types/types';
import IconButton from '@/components/IconButton';
import TrophyIcon from '../icons/TrophyIcon';
import CircleAddIcon from '../icons/CircleAddIcon';
import ChevronRightIcon from '../icons/ChevronRightIcon';
import { useQuery } from '@tanstack/react-query';
import type { Competition } from '@/api/model/competition';
import { UserRole } from '@/api/model/profile';
import Chip from '@/components/Chip';
import Loading from '@/components/Loading';
import ISO8601 from '@/util/iso-dates'
import { classes } from '@/util/component-utils';
import { getAssetThumbnailForCompetitionWithId, getAssetThumbnailJumpWithId } from '@/api/service/asset.service';
import { getAllCompetitions, getAllJumps } from '@/api/service/competition.service';
import { JumpStatus } from '@/models/jump';
import { PersonRole } from '@/models/person';
import { type UrlResponse } from '@/models/url-response';
import { keyCompetitionAll, keyCompetitionJumps, keyAssetThumbnailCompetitionWithId, keyAssetThumbnailJumpWithId } from '@/api/query-keys';
import { UserNotificationManager } from '@/util/user-notification-manager';
import styles from './SidebarMenu.module.css';

interface SidebarProps {
    onClose?: CallbackFunc;
    role?: UserRole;
}

interface CompetitionProps extends SidebarProps {
    competition: Competition;
}

interface MenuHeaderProps {
    title: string;
    action?: VoidFunction;
}

function FallbackImage() {
    let classes = [styles.item__image, styles.item__fallback];
    return(
        <div className={classes.join(' ')}>
            <TrophyIcon size={{ width: 32, height: 32 }} fill="#0090f2" />
        </div>
    )
}

interface ItemImageProps {
    url?: string;
}

function ItemImage(props: ItemImageProps) {
    let classes = [styles.item__image];
    let url = props.url;
    if (url && !url.startsWith("s3://")) {
        classes.push(styles.thumbnail);
        const imageStyle = {
             backgroundImage: `url(${url})`
        };
        return(
            <div className={classes.join(' ')} style={imageStyle}>
            </div>
        )
    }
        
    return (<FallbackImage />);
}

interface ThumbnailProps {
    jumpId?: string;
    competitionId?: string;
}

function Thumbnail(props: ThumbnailProps) {

    const queryKey = () =>{
        let result: string[] = [];
        if (props.jumpId) {
            result = keyAssetThumbnailJumpWithId(props.jumpId);
        } else if (props.competitionId) {
            result = keyAssetThumbnailCompetitionWithId(props.competitionId);
        }
        console.log(`QUERY KEY: ${result}`);
        return result;
    }

    const thumbFunc = async (): Promise<UrlResponse> => {
        let result: UrlResponse | undefined;
        if (props.jumpId) {
            result = await getAssetThumbnailJumpWithId(props.jumpId);
        } else if (props.competitionId) {
            result = await getAssetThumbnailForCompetitionWithId(props.competitionId);
        }
        if (!result) 
        {
            throw new Error("Not found");
        }
        return result;
    }

    const { data } = useQuery({
        queryKey: queryKey(), 
        queryFn: thumbFunc
    });

    if (!data || !data.url || (!props.competitionId && !props.jumpId)) {
        return (
            <FallbackImage />
        )
    }

    return (<ItemImage url={data?.url} />)
}

function CompetitionItem(props: CompetitionProps) {
    const navigate = useNavigate();
    
    const onClose = () => {
        if (props.onClose) {
            props.onClose();
        }
    }

    const handleRoute = () => () => {
        const route = `/leaderboard/${ props.competition.id }`;
        setTimeout(() => {
            navigate({ to: route, viewTransition: true });
        })
        onClose();
    }
    
    const scheduleEnd = (competition: Competition): Date | undefined => {
        if (!competition.schedule) return;
        let end: Date | undefined;
        for (const round of competition.schedule) {
            if (round.end && ISO8601.isValid(round.end) ) {
                const roundEndDate = ISO8601.toDate(round.end);
                if (end) {
                    if (isAfter(roundEndDate, end)) {
                        end = roundEndDate;
                    }
                } else {
                    end = roundEndDate;
                }
            }
        }
        return end;

    }

    const end = scheduleEnd(props.competition);
    const ended = end ? isAfter(new Date(), end) : false;

    return (
        <div className={styles.item} onClick={ handleRoute() }>
            <div className={styles.item__left}>
                <Thumbnail competitionId={props.competition.id} />
            </div>
            <div className={styles.item__center}>
                <p className={styles.item__title}>{props.competition.name}</p>
                <div>
                { ended && <Chip label="Ended" className={styles.badge} size="small" /> }
                </div>
            </div>
            <div className={styles.item__right}>
                <ChevronRightIcon size={{ width: 24, height: 24 }} fill="#7D7D7D" className={ classes(styles.item__chevron, 'softened') } />
            </div>
        </div>
    );
}

interface JumpProps extends SidebarProps {
    jumpId: string;
    name: string;
    status: JumpStatus;
    jumpRole: PersonRole;
}

function JumpItem(props: JumpProps) {
    const navigate = useNavigate();

    const draft = (props.status === JumpStatus.DRAFT);
    
    const onClose = () => {
        if (props.onClose) {
            props.onClose();
        }
    }

    const handleRoute = () => () => {
        const page = draft ? "draftsubmission" : "pubsubmission";
        const route = `/${page}/${props.jumpId}`;
        setTimeout(() => {
            navigate({ to: route, viewTransition: true });
        });
        onClose();
    }

    return (
        <div className={styles.item} onClick={ handleRoute() }>
            <div className={styles.item__left}>
                <Thumbnail jumpId={props.jumpId} />
            </div>
            <div className={styles.item__center}>
                <p className={styles.item__title}>{props.name}</p>
                <div>
                { !draft && <Chip label="Submitted" className={styles.badge} color="success" size="small" /> }
                { (draft && props.jumpRole === PersonRole.REVIEWER) && <Chip label="Reviewer" className={styles.badge} color="secondary" size="small" /> }
                </div>
            </div>
            <div className={styles.item__right}>
                <ChevronRightIcon size={{ width: 24, height: 24 }} fill="#7D7D7D" className={ classes(styles.item__chevron, 'softened') } />
            </div>
        </div>
    );
}

function MenuHeader(props: MenuHeaderProps) {
    const addButton = () => {
        if (!props.action) {
            return (<></>);
        }
        return (
            <IconButton className={styles.newItemButton} onClick={props.action}>
                <CircleAddIcon fill="#0090f2" />
            </IconButton>
        )
    }

    return( 
        <div className={styles.newItemRow}>
            <h5 className={styles.menu__title}>{props.title}</h5>
            { addButton() }
        </div>
    )
}

// function MenuItems(props: PropsWithChildren) {
//     return (<ul>{props.children}</ul>);
// }

// function MenuItem(props: PropsWithChildren) {
//     return (<li>{props.children}</li>);
// }

function CompetitionsMenu(props: SidebarProps) {
    const { data, error, isLoading } = useQuery({
        queryKey: keyCompetitionAll(), 
        queryFn: async () => getAllCompetitions(props.role)
    });
    // const navigate = useNavigate();

    const onClose = () => {
        if (props.onClose) {
            props.onClose();
        }
    }

    const onNewCompetitionClick = () => {
        alert("Not yet. Create a new competition by uploading a video or PDF.");
        //navigate({ to: "/home", viewTransition: true, search: { ne: true }  })
    }
    let addButtonProps;
    if (props.role === UserRole.ORGANIZER) {
        addButtonProps = {
            action: onNewCompetitionClick,
        }
    }

    if (isLoading) {
        return (
            <div className={styles.menu}>
                <MenuHeader title="Competitions" />
                <Loading />
            </div>
        );
    }
 
    if (error) {
        console.log(error);
        UserNotificationManager.reportError("Unable to load competitions", error);
        return null;
    }

    if (data == null || data.length === 0) {
        return (
            <div className={styles.menu}>
                <MenuHeader title="Competitions" {...addButtonProps} />
                <div className="empty-result">
                    no competitions
                </div>
            </div>
        );
    }

    return (
        <div className={styles.menu}>
            <MenuHeader title="Competitions" {...addButtonProps} />
            <ul>
                {data.map(competition => (
                    <li className={styles.item}  key={competition.id}>
                        <CompetitionItem competition={competition} onClose={onClose} />
                    </li>
                ))}
            </ul>
        </div>
    );
}

function JumpsMenu(props: SidebarProps) {
    const { data, error, isLoading } = useQuery({
        queryKey: keyCompetitionJumps(), 
        queryFn: async () => getAllJumps(JumpStatus.DRAFT)
    });
    // const navigate = useNavigate();

    const onClose = () => {
        if (props.onClose) {
            props.onClose();
        }
    }

    const onNewJumpClick = () => {
        alert("Not yet. Create a new jump by uploading a video or PDF.");
        //navigate({ to: "/home", viewTransition: true, search: { ne: true } })
    }

    let addButtonProps;
    if (props.role === UserRole.COMPETITOR) {
        addButtonProps = {
            action: onNewJumpClick,
        }
    }

    if (isLoading) {
        return (
            <div className={styles.menu}>
                <MenuHeader title="Jumps" />
                <Loading />
            </div>
        );
    }

    if (error) {
        console.log(error);
        UserNotificationManager.reportError("Unable to load jumps", error);
        return null;
    }

    if (data == null || data.length === 0) {
        return (
            <div className={styles.menu}>
                <MenuHeader title="Jumps" {...addButtonProps} />
                <div className="empty-result">
                    no jumps
                </div>
            </div>
        );
    }

    return (
        <div className={styles.menu}>
            <MenuHeader title="Jumps" {...addButtonProps } />
            <ul>
                {data.map(jump => (
                    <li className={styles.item}  key={jump.id}>
                        <JumpItem jumpId={jump.id!} 
                            status={jump.status!} 
                            name={jump.name!}
                            onClose={onClose}
                            jumpRole={jump.userRole ?? PersonRole.JUMP_ADMIN} />
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function SidebarMenu(props: SidebarProps) {
    const role = useAuthorization(userRoleSelector);

    const onClose = () => () => {
        if (props.onClose) {
            props.onClose();
        }
    };
  
    return (
        <div className={styles.sidebar}>
            <CompetitionsMenu onClose={onClose} role={role} />
            <JumpsMenu onClose={onClose} role={role} />
        </div>
    );
}
